import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { ComponentType } from 'react'
import { AppDownloadModalProps } from '@smuchile/pkg-unimarc-components'
import { useContentful, useMobile } from '@smuchile/pkg-unimarc-hooks'
import { IInAppModal } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IInAppModal'
import { AppDownloadModalProvider } from './context'
import { useAppDownloadModalVisibility } from './useAppDownloadModalVisibility'

type AppDownloadModalContentful = Omit<IInAppModal, 'header' | 'subtitle'> & {
  AndroidStoreIcon?: unknown
  AndroidStoreUrl?: string
  CommonIcon?: unknown
  CommonUrl?: string
  HeaderImage?: unknown
  OpenDelaySeconds?: number | string
  ReopenAfterMinutes?: number | string
  IosStoreIcon?: unknown
  IosStoreUrl?: string
  UnimarcIcon?: unknown
  androidStoreIcon?: unknown
  androidStoreUrl?: string
  commonIcon?: unknown
  commonUrl?: string
  headerImage?: unknown
  iosStoreIcon?: unknown
  iosStoreUrl?: string
  openDelaySeconds?: number | string
  reopenAfterMinutes?: number | string
  unimarcIcon?: unknown
}

interface AppDownloadModalWrapperProps {
  children: React.ReactNode
}

type AppDownloadModalComponentProps = Omit<
  AppDownloadModalProps,
  'header' | 'subtitle'
> & {
  commonStoreIconSrc?: string
  commonStoreUrl?: string
  header?: string
  headerImageSrc?: string
  subtitle?: string
}

const AppDownloadModalComponent = dynamic<AppDownloadModalComponentProps>(
  () => {
    return import('@smuchile/pkg-unimarc-components').then((mod) => {
      return mod.AppDownloadModal as ComponentType<AppDownloadModalComponentProps>
    })
  }
)

const getContentfulAssetReferenceId = (field?: unknown): string => {
  if (!field || typeof field !== 'object') return ''
  const sys = field?.['sys']

  return typeof sys?.id === 'string' ? sys.id : ''
}

const getContentfulAssetUrl = (asset?: unknown): string => {
  if (!asset || typeof asset !== 'object') return ''

  return asset?.['fields']?.['file']?.['url'] || ''
}

const getContentfulAssetById = (assets: unknown[] = [], id?: string) => {
  if (!id) return null

  return assets.find((asset) => {
    return asset?.['sys']?.['id'] === id
  })
}

const getContentfulEntryId = (entry?: unknown): string => {
  if (!entry || typeof entry !== 'object') return ''
  const sys = entry?.['sys']

  return typeof sys?.id === 'string' ? sys.id : ''
}

const getNumberConfig = (value: unknown, fallback: number): number => {
  const numericValue = Number(value)

  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : fallback
}

export const AppDownloadModalWrapper = ({
  children
}: AppDownloadModalWrapperProps): React.ReactElement => {
  const router = useRouter()
  const { isMobile: isMobileUserAgent } = useMobile()
  const isSourceApp = router?.query?.source === 'APP'
  const shouldEvaluateAppDownloadModal = isMobileUserAgent && !isSourceApp

  const appDownloadModalResult = useContentful({
    options: {
      content_type: 'inappMobile',
      include: 1,
      limit: 1,
      'fields.idFormato': 1,
      'fields.startDate[lte]': new Date().toISOString(),
      'fields.endDate[gte]': new Date().toISOString(),
      'fields.platform': 'Web Unimarc eComm Mobile',
      'fields.status': true
    },
    reactQuery: {
      enabled: shouldEvaluateAppDownloadModal,
      queryKey: 'appDownloadModalMobile'
    }
  })

  const appDownloadModalEntry = appDownloadModalResult?.data?.['items']?.[0]
  const appDownloadModal: AppDownloadModalContentful =
    appDownloadModalEntry?.fields || null
  const appDownloadModalCampaignId = getContentfulEntryId(appDownloadModalEntry)
  const appDownloadModalAssets =
    appDownloadModalResult.data?.['includes']?.Asset || []
  const appDownloadModalIconId = getContentfulAssetReferenceId(
    appDownloadModal?.UnimarcIcon || appDownloadModal?.unimarcIcon
  )
  const appDownloadModalHeaderImageId = getContentfulAssetReferenceId(
    appDownloadModal?.HeaderImage || appDownloadModal?.headerImage
  )
  const appDownloadModalHeaderImage = getContentfulAssetById(
    appDownloadModalAssets,
    appDownloadModalHeaderImageId
  )
  const appDownloadModalCommonIconId = getContentfulAssetReferenceId(
    appDownloadModal?.CommonIcon || appDownloadModal?.commonIcon
  )
  const appDownloadModalCommonIcon = getContentfulAssetById(
    appDownloadModalAssets,
    appDownloadModalCommonIconId
  )
  const appDownloadModalIosStoreIconId = getContentfulAssetReferenceId(
    appDownloadModal?.IosStoreIcon || appDownloadModal?.iosStoreIcon
  )
  const appDownloadModalIosStoreIcon = getContentfulAssetById(
    appDownloadModalAssets,
    appDownloadModalIosStoreIconId
  )
  const appDownloadModalAndroidStoreIconId = getContentfulAssetReferenceId(
    appDownloadModal?.AndroidStoreIcon || appDownloadModal?.androidStoreIcon
  )
  const appDownloadModalAndroidStoreIcon = getContentfulAssetById(
    appDownloadModalAssets,
    appDownloadModalAndroidStoreIconId
  )
  const appDownloadModalExcludedAssetIds = [
    appDownloadModalIconId,
    appDownloadModalHeaderImageId,
    appDownloadModalCommonIconId,
    appDownloadModalIosStoreIconId,
    appDownloadModalAndroidStoreIconId
  ].filter(Boolean)
  const appDownloadModalImage = appDownloadModalAssets.find((asset) => {
    return (
      appDownloadModalExcludedAssetIds.indexOf(asset?.['sys']?.['id']) === -1
    )
  })
  const appDownloadModalOpenDelaySeconds = getNumberConfig(
    appDownloadModal?.OpenDelaySeconds ?? appDownloadModal?.openDelaySeconds,
    0
  )
  const appDownloadModalReopenAfterMinutes = getNumberConfig(
    appDownloadModal?.ReopenAfterMinutes ??
      appDownloadModal?.reopenAfterMinutes,
    60
  )
  const appDownloadModalHeaderImageSrc = getContentfulAssetUrl(
    appDownloadModalHeaderImage ||
      appDownloadModal?.HeaderImage ||
      appDownloadModal?.headerImage
  )
  const appDownloadModalCommonIconSrc = getContentfulAssetUrl(
    appDownloadModalCommonIcon ||
      appDownloadModal?.CommonIcon ||
      appDownloadModal?.commonIcon
  )
  const appDownloadModalCommonUrl =
    appDownloadModal?.CommonUrl || appDownloadModal?.commonUrl
  const appDownloadModalIosStoreIconSrc = getContentfulAssetUrl(
    appDownloadModalIosStoreIcon ||
      appDownloadModal?.IosStoreIcon ||
      appDownloadModal?.iosStoreIcon
  )
  const appDownloadModalAndroidStoreIconSrc = getContentfulAssetUrl(
    appDownloadModalAndroidStoreIcon ||
      appDownloadModal?.AndroidStoreIcon ||
      appDownloadModal?.androidStoreIcon
  )
  const appDownloadModalFooterImageSrc = getContentfulAssetUrl(
    appDownloadModalImage
  )
  const hasAppDownloadModalCommonButton = Boolean(
    appDownloadModalCommonIconSrc && appDownloadModalCommonUrl
  )
  const isAppDownloadModalValid = Boolean(
    shouldEvaluateAppDownloadModal &&
      appDownloadModalCampaignId &&
      (appDownloadModalHeaderImageSrc ||
        hasAppDownloadModalCommonButton ||
        appDownloadModalIosStoreIconSrc ||
        appDownloadModalAndroidStoreIconSrc ||
        appDownloadModalFooterImageSrc)
  )
  const { handleDismiss, handleStoreClick, isBlockingLegacy, shouldShow } =
    useAppDownloadModalVisibility({
      campaignId: appDownloadModalCampaignId,
      isCampaignValid: isAppDownloadModalValid,
      isLoading: appDownloadModalResult?.isLoading,
      openDelaySeconds: appDownloadModalOpenDelaySeconds,
      reopenAfterMinutes: appDownloadModalReopenAfterMinutes,
      shouldEvaluate: shouldEvaluateAppDownloadModal
    })

  return (
    <AppDownloadModalProvider
      isAppDownloadModalBlockingLegacy={isBlockingLegacy}
    >
      {shouldShow && (
        <AppDownloadModalComponent
          androidStoreIconSrc={appDownloadModalAndroidStoreIconSrc}
          androidStoreUrl={
            appDownloadModal?.AndroidStoreUrl ||
            appDownloadModal?.androidStoreUrl
          }
          commonStoreIconSrc={appDownloadModalCommonIconSrc}
          commonStoreUrl={appDownloadModalCommonUrl}
          headerImageSrc={appDownloadModalHeaderImageSrc}
          imageSrc={appDownloadModalFooterImageSrc}
          iosStoreIconSrc={appDownloadModalIosStoreIconSrc}
          iosStoreUrl={
            appDownloadModal?.IosStoreUrl || appDownloadModal?.iosStoreUrl
          }
          isMobile={isMobileUserAgent}
          onStoreClick={handleStoreClick}
          onToggle={handleDismiss}
          open={shouldShow}
          redirectTo={appDownloadModal?.actionUrl}
        />
      )}
      {children}
    </AppDownloadModalProvider>
  )
}
